/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { createContext, type ForwardedRef, forwardRef, useMemo, useRef, useState } from 'react';
import { Cell as CellCore, type CellProps as CellCoreProps, type ContextValue, Focusable, Provider, TooltipTrigger, useContextProps } from 'react-aria-components';
import { FullScreenStrokeIcon } from '../../icons/full-screen-stroke/full-screen-stroke.icon';
import { $ } from '../../tokens.stylex';
import { AvatarContext } from '../avatar/avatar.component';
import { BadgeContext } from '../badge/badge.component';
import { IconButtonContext } from '../icon-button/icon-button.component';
import { IconContext } from '../icon/icon.component';
import { LinkContext } from '../link/link.component';
import { Text, TextContext } from '../text/text.component';
import { Tooltip } from '../tooltip/tooltip.component';

interface CellProps extends Omit<CellCoreProps, 'style' | 'className'>, Omit<CellCoreProps, 'style' | 'className'> {
  /** The alignment of the cell. */
  align?: 'right' | 'left' | 'padded';
  /** Whether the cell has no right border. @default false */
  isBorderless?: boolean;
  /** Whether the table is displayed in full screen mode. @default false */
  isFullScreen?: boolean;
  /** Whether the cell has low priority styling (quaternary text color). Default: false. */
  isLowPriority?: boolean;
  /** Text to display in tooltip when content is truncated. Required for tooltips to appear. */
  textValue?: string;
  /** Delay in milliseconds before checking overflow on hover. Default: 500ms. Set to 0 for instant tooltip. */
  tooltipDelay?: number;
  style?: stylex.StyleXStyles;
}

const CellContext = createContext<ContextValue<CellProps, HTMLDivElement>>({});

const Cell = forwardRef((props: CellProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, CellContext);

  const {
    style,
    children,
    align = 'left',
    isBorderless = false,
    isFullScreen = false,
    isLowPriority = false,
    textValue,
    tooltipDelay = 500,
    ...coreProps
  } = props;

  console.log(align,
textValue);

  const linkContextValue = useMemo(() => ({
    size: 14 as const,
    style: linkStyles.base,
  }), []);

  const textContextValue = useMemo(() => ({
    size: 14 as const,
    color: isLowPriority ? 'quaternary' as const : 'primary' as const,
    style: textStyles.base,
  }), [isLowPriority]);

  const avatarContextValue = useMemo(() => ({
    size: 16 as const,
    style: avatarStyles.base,
  }), []);

  const badgeContextValue = useMemo(() => ({
    style: badgeStyles.base,
  }), []);

  const iconContextValue = useMemo(() => ({
    slots: {
      [CELL_SLOTS.INTERNAL_LINK_ICON]: {
        children: <FullScreenStrokeIcon />,
        color: 'tertiary' as const,
        style: internalLinkIconStyles.base,
        size: 18
      },
        [CELL_SLOTS.FLOATING_INDICATOR]: {
        children: <FullScreenStrokeIcon />,
        color: 'tertiary' as const,
        style: internalLinkIconStyles.reversed,
        size: 18
      }
    }
  }), []);

  return (
    <CellCore
      ref={ref}
      {...stylex.props(
        styles.base,
        align === 'right' && styles.right,
        align === 'padded' && styles.padded,
        !isFullScreen && align === 'padded' && styles.embeddedPadded,
        !isFullScreen && isBorderless && styles.borderless,
        style)}
      {...coreProps}>
      {(renderProps) => {
        let evaluatedChildren = typeof children === 'function' ? children(renderProps) : children;
        return (
          <Provider
            values={[
              [LinkContext, linkContextValue],
              [TextContext, textContextValue],
              [AvatarContext, avatarContextValue],
              [BadgeContext, badgeContextValue],
              [IconContext, iconContextValue]
            ]}>
            <CellContent_INTERNAL
              isTextOnly={false}
              evaluatedChildren={evaluatedChildren}
              align={align}
              textValue={textValue}
              tooltipDelay={tooltipDelay}
            />
          </Provider>
        );
      }}
    </CellCore >
  );
});

interface CellContentProps {
  isTextOnly: boolean;
  evaluatedChildren: React.ReactNode;
  align: 'right' | 'left' | 'padded';
  textValue?: string;
  tooltipDelay: number;
}

const CellContent_INTERNAL = ({ isTextOnly, evaluatedChildren, align, textValue, tooltipDelay }: CellContentProps) => {
  if (!textValue) {
    return (
      <Provider
        values={[
          [IconButtonContext, {
            size: 28,
            variant: 'ghost',
          }]
        ]}>
        <div {...stylex.props(cellContentStyles.simpleContainer(align))}>
          {isTextOnly ? <Text>{evaluatedChildren}</Text> : evaluatedChildren}
        </div>
      </Provider>
    );
  }

  return (
    <CellContentWithTooltip
      isTextOnly={isTextOnly}
      evaluatedChildren={evaluatedChildren}
      align={align}
      textValue={textValue}
      tooltipDelay={tooltipDelay}
    />
  );
};

const CellContentWithTooltip = ({ isTextOnly, evaluatedChildren, align, textValue, tooltipDelay }: Omit<CellContentProps, 'renderProps'> & { textValue: string }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [shouldShowTooltip, setShouldShowTooltip] = useState(false);
  const hoverTimeoutRef = useRef<number | null>(null);
  const isHoveredRef = useRef(false);

  const handleMouseEnter = () => {
    isHoveredRef.current = true;

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    hoverTimeoutRef.current = window.setTimeout(() => {
      if (!isHoveredRef.current) return;

      const element = contentRef.current;
      if (!element) return;

      // Check children for truncation (flex items handle their own overflow)
      const target = element.firstElementChild as HTMLElement ?? element;
      const isTruncated =
        target.offsetWidth < target.scrollWidth ||
        target.offsetHeight < target.scrollHeight;

      setShouldShowTooltip(isTruncated && textValue.length > 0);
    }, tooltipDelay);
  };

  const handleMouseLeave = () => {
    isHoveredRef.current = false;

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    setShouldShowTooltip(false);
  };

  return (
    <Provider
      values={[
        [IconButtonContext, {
          size: 28,
          variant: 'ghost',
        }]
      ]}>
      <TooltipTrigger isOpen={shouldShowTooltip}>
        <Focusable isDisabled>
          <div
            role="button"
            tabIndex={-1}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            {...stylex.props(cellContentStyles.tooltipTrigger(align))}>
            <div ref={contentRef} role="button" {...stylex.props(cellContentStyles.base)}>
              {isTextOnly ? <Text>{evaluatedChildren}</Text> : evaluatedChildren}
            </div>
          </div>
        </Focusable>
        <Tooltip>
          {textValue}
        </Tooltip>
      </TooltipTrigger>
    </Provider>
  );
};

const styles = stylex.create({
  base: {
    overflow: 'hidden',
    alignContent: 'center',
    boxSizing: 'border-box',
    cursor: 'default',
    position: 'relative',
    whiteSpace: 'nowrap',
    borderBottomColor: $.border06,
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    borderRightColor: $.border06,
    borderRightStyle: 'solid',
    borderRightWidth: 1,
    height: 36,
  },
  primary: { color: $.textPrimary },
  quaternary: { color: $.textQuaternary },
  borderless: { borderRightWidth: 0 },
  embeddedPadded: { paddingLeft: 8 },
  padded: { paddingLeft: 20 },
  right: { textAlign: 'end', },
});

const linkStyles = stylex.create({
  base: {
    overflow: 'hidden',
    display: 'inline',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    height: '100%',
  },
});

const textStyles = stylex.create({
  base: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
  },
});

const avatarStyles = stylex.create({
  base: {
    lineHeight: '100%',
    transform: 'translateY(3px)',
    paddingRight: 4,
  }
});

const badgeStyles = stylex.create({
  base: {
    maxWidth: '100%',
    minWidth: 0,
    overflow: 'hidden',
  }
});

const cellContentStyles = stylex.create({
  base: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
    display: 'flex',
    height: '100%',
    alignItems: 'center',
  },
  simpleContainer: (align: 'right' | 'left' | 'padded') => ({
    overflow: 'hidden',
    alignItems: 'center',
    display: 'flex',
    justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
    height: '100%',
    paddingLeft: align === 'padded' ? 0 : 12,
    paddingRight: 8,
    width: '100%',
  }),
  tooltipTrigger: (align: 'right' | 'left' | 'padded') => ({
    overflow: 'hidden',
    alignItems: 'center',
    display: 'flex',
    justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
    height: '100%',
    paddingLeft: align === 'padded' ? 0 : 12,
    paddingRight: 8,
    width: '100%',
  }),
});

const internalLinkIconStyles = stylex.create({
  base: {
    marginLeft: {
      default: null,
      [stylex.when.ancestor(':is(:hover, :focus-within)')]: 'auto'
    },
    width: {
      default: 0,
      [stylex.when.ancestor(':is(:hover, :focus-within)')]: 18,
    },
    minWidth: {
      default: 0,
      [stylex.when.ancestor(':is(:hover, :focus-within)')]:  18,
    },
    display: {
      default: 'none',
      [stylex.when.ancestor(':is(:hover, :focus-within)')]: 'unset',
    },
  },
  reversed: {
    marginLeft: 'auto',
    width: {
      default: null,
      [stylex.when.ancestor(':is(:hover, :focus-within)')]: 0,
    },
    minWidth: {
      default: null,
      [stylex.when.ancestor(':is(:hover, :focus-within)')]:  0,
    },
    opacity: {
      default: 1,
      [stylex.when.ancestor(':is(:hover, :focus-within)')]: 0,
    },
  }
});

const CELL_SLOTS = {
  INTERNAL_LINK_ICON: 'internal-link-icon',
  FLOATING_INDICATOR: 'floating-indicator'
};

export { Cell, CELL_SLOTS, CellContext, type CellProps };
